import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { format, subDays, startOfMonth, endOfMonth, isWithinInterval, parseISO } from "date-fns";
import {
  FileDown, FileSpreadsheet, FileText, Filter, Search, Calendar,
  RefreshCw, Download, ChevronDown, X, CheckCircle2, Clock, Beaker
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Status badge colors
const statusColors = {
  "Not Started": "bg-slate-100 text-slate-700",
  "Started": "bg-blue-100 text-blue-700",
  "End": "bg-amber-100 text-amber-700",
  "Complete": "bg-emerald-100 text-emerald-700",
  "Finish": "bg-green-100 text-green-700",
};

// Date range presets
const datePresets = [
  { label: "Today", getValue: () => ({ from: new Date(), to: new Date() }) },
  { label: "Last 7 Days", getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: "Last 30 Days", getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: "This Month", getValue: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  { label: "All Time", getValue: () => ({ from: null, to: null }) },
];

export default function ReportsPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedPreset, setSelectedPreset] = useState("All Time");

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/product-folders`);
      if (response.data.success) {
        setProducts(response.data.folders || []);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Apply filters
  useEffect(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.productName?.toLowerCase().includes(query) ||
        p.sampleNo?.toLowerCase().includes(query) ||
        p.clientName?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter(p => p.testStatus === statusFilter);
    }

    // Date range filter
    if (dateRange.from && dateRange.to) {
      filtered = filtered.filter(p => {
        if (!p.createdAt) return true;
        try {
          const createdDate = typeof p.createdAt === 'string' ? parseISO(p.createdAt) : new Date(p.createdAt);
          return isWithinInterval(createdDate, { start: dateRange.from, end: dateRange.to });
        } catch {
          return true;
        }
      });
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, statusFilter, dateRange]);

  // Export functions
  const exportToCSV = () => {
    setExporting(true);
    try {
      const headers = ["Sample No", "Product Name", "Client", "Status", "Iterations", "Created Date", "Sync Status"];
      const rows = filteredProducts.map(p => [
        p.sampleNo || "",
        p.productName || "",
        p.clientName || "",
        p.testStatus || "",
        p.iterations?.length || 0,
        p.createdAt ? format(new Date(p.createdAt), "dd-MMM-yyyy") : "",
        p.syncStatus || ""
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `VCC_Products_Report_${format(new Date(), "yyyy-MM-dd")}.csv`;
      link.click();
      toast.success("CSV exported successfully!");
    } catch (error) {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      // Create a more detailed Excel-like CSV with formatting
      const headers = [
        "Sample No", "Product Name", "Client Name", "Category", "SKU",
        "Test Status", "Iterations Count", "Sync Status", "Created Date", "Last Updated"
      ];
      
      const rows = filteredProducts.map(p => [
        p.sampleNo || "",
        p.productName || "",
        p.clientName || "",
        p.productCategory || "",
        p.sku || "",
        p.testStatus || "",
        p.iterations?.length || 0,
        p.syncStatus || "",
        p.createdAt ? format(new Date(p.createdAt), "dd-MMM-yyyy HH:mm") : "",
        p.portalUpdatedAt ? format(new Date(p.portalUpdatedAt), "dd-MMM-yyyy HH:mm") : ""
      ]);

      // Add summary row
      const summaryRows = [
        [],
        ["SUMMARY"],
        ["Total Products", filteredProducts.length],
        ["Not Started", filteredProducts.filter(p => p.testStatus === "Not Started").length],
        ["In Progress", filteredProducts.filter(p => p.testStatus === "Started").length],
        ["Completed", filteredProducts.filter(p => ["Complete", "Finish"].includes(p.testStatus)).length],
        ["Report Generated", format(new Date(), "dd-MMM-yyyy HH:mm:ss")]
      ];

      const csvContent = [
        "V COSMETIC CRAFTERS - PRODUCT REPORT",
        `Generated: ${format(new Date(), "dd-MMM-yyyy HH:mm:ss")}`,
        "",
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(",")),
        ...summaryRows.map(row => row.join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `VCC_Products_Report_${format(new Date(), "yyyy-MM-dd")}.xls`;
      link.click();
      toast.success("Excel exported successfully!");
    } catch (error) {
      toast.error("Failed to export Excel");
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = () => {
    setExporting(true);
    try {
      // Create printable HTML and open print dialog
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>VCC Product Report</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; }
            h1 { color: #0D9488; border-bottom: 2px solid #F472B6; padding-bottom: 10px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .summary { background: #FDF2F8; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .summary-item { display: inline-block; margin-right: 30px; }
            .summary-label { font-size: 12px; color: #666; }
            .summary-value { font-size: 24px; font-weight: bold; color: #0D9488; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #0D9488; color: white; padding: 12px 8px; text-align: left; font-size: 12px; }
            td { padding: 10px 8px; border-bottom: 1px solid #E5E7EB; font-size: 11px; }
            tr:nth-child(even) { background: #F9FAFB; }
            .status { padding: 4px 8px; border-radius: 4px; font-size: 10px; font-weight: 500; }
            .status-not-started { background: #F1F5F9; color: #475569; }
            .status-started { background: #DBEAFE; color: #1D4ED8; }
            .status-complete { background: #D1FAE5; color: #059669; }
            .footer { margin-top: 30px; text-align: center; color: #666; font-size: 10px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>V Cosmetic Crafters - Product Report</h1>
          <div class="header">
            <div>Generated: ${format(new Date(), "dd-MMM-yyyy HH:mm:ss")}</div>
            <div>Total Records: ${filteredProducts.length}</div>
          </div>
          <div class="summary">
            <div class="summary-item">
              <div class="summary-label">TOTAL PRODUCTS</div>
              <div class="summary-value">${filteredProducts.length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">NOT STARTED</div>
              <div class="summary-value">${filteredProducts.filter(p => p.testStatus === "Not Started").length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">IN PROGRESS</div>
              <div class="summary-value">${filteredProducts.filter(p => p.testStatus === "Started").length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">COMPLETED</div>
              <div class="summary-value">${filteredProducts.filter(p => ["Complete", "Finish"].includes(p.testStatus)).length}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Sample No</th>
                <th>Product Name</th>
                <th>Client</th>
                <th>Status</th>
                <th>Iterations</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              ${filteredProducts.map(p => `
                <tr>
                  <td>${p.sampleNo || "-"}</td>
                  <td>${p.productName || "-"}</td>
                  <td>${p.clientName || "-"}</td>
                  <td><span class="status status-${(p.testStatus || "").toLowerCase().replace(" ", "-")}">${p.testStatus || "-"}</span></td>
                  <td>${p.iterations?.length || 0}</td>
                  <td>${p.createdAt ? format(new Date(p.createdAt), "dd-MMM-yyyy") : "-"}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          <div class="footer">
            V Cosmetic Crafters Private Limited - Confidential Report
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        // Use safer DOM manipulation instead of document.write (XSS prevention)
        printWindow.document.open();
        const doc = printWindow.document;
        doc.write('<!DOCTYPE html><html><head></head><body></body></html>');
        doc.close();
        
        // Set content safely using textContent/innerHTML after document is closed
        doc.documentElement.innerHTML = printContent.replace(/<!DOCTYPE html>|<html>|<\/html>/gi, '');
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
      
      toast.success("PDF print dialog opened!");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateRange({ from: null, to: null });
    setSelectedPreset("All Time");
  };

  // Stats
  const stats = {
    total: filteredProducts.length,
    notStarted: filteredProducts.filter(p => p.testStatus === "Not Started").length,
    inProgress: filteredProducts.filter(p => p.testStatus === "Started").length,
    completed: filteredProducts.filter(p => ["Complete", "Finish"].includes(p.testStatus)).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-poppins font-bold text-slate-800">Reports</h1>
            <p className="text-slate-500 font-poppins">Generate and export product reports</p>
          </div>
          
          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              disabled={exporting || filteredProducts.length === 0}
              className="border-vcc-teal text-vcc-teal hover:bg-vcc-teal/10 font-poppins"
              data-testid="export-csv-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button
              variant="outline"
              onClick={exportToExcel}
              disabled={exporting || filteredProducts.length === 0}
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-poppins"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              onClick={exportToPDF}
              disabled={exporting || filteredProducts.length === 0}
              className="bg-vcc-pink-400 hover:bg-vcc-pink-500 text-white font-poppins"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white/90 border-vcc-pink-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-vcc-teal/10 flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-vcc-teal" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-poppins uppercase">Total</p>
                  <p className="text-2xl font-bold text-slate-800 font-poppins">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90 border-vcc-pink-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-poppins uppercase">Not Started</p>
                  <p className="text-2xl font-bold text-slate-800 font-poppins">{stats.notStarted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90 border-vcc-pink-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-poppins uppercase">In Progress</p>
                  <p className="text-2xl font-bold text-slate-800 font-poppins">{stats.inProgress}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90 border-vcc-pink-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-poppins uppercase">Completed</p>
                  <p className="text-2xl font-bold text-slate-800 font-poppins">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white/90 border-vcc-pink-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-poppins text-slate-800 flex items-center gap-2">
                <Filter className="w-5 h-5 text-vcc-teal" />
                Filters
              </CardTitle>
              {(searchQuery || statusFilter !== "all" || dateRange.from) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 font-poppins">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 font-poppins border-vcc-pink-100"
                  data-testid="search-input"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="font-poppins border-vcc-pink-100" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="Started">In Progress</SelectItem>
                  <SelectItem value="End">Testing</SelectItem>
                  <SelectItem value="Complete">Complete</SelectItem>
                  <SelectItem value="Finish">Finish</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Range Preset */}
              <Select 
                value={selectedPreset} 
                onValueChange={(value) => {
                  setSelectedPreset(value);
                  const preset = datePresets.find(p => p.label === value);
                  if (preset) {
                    setDateRange(preset.getValue());
                  }
                }}
              >
                <SelectTrigger className="font-poppins border-vcc-pink-100" data-testid="date-preset">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select Date Range" />
                </SelectTrigger>
                <SelectContent>
                  {datePresets.map(preset => (
                    <SelectItem key={preset.label} value={preset.label}>{preset.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Custom Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start font-poppins border-vcc-pink-100">
                    <Calendar className="w-4 h-4 mr-2" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd")}`
                      ) : format(dateRange.from, "MMM dd, yyyy")
                    ) : (
                      "Custom Range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range || { from: null, to: null });
                      setSelectedPreset("Custom");
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="bg-white/90 border-vcc-pink-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-poppins text-slate-800">
              Product Data ({filteredProducts.length} records)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-vcc-teal" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-slate-400 font-poppins">
                No products found matching your filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-vcc-cream-50">
                      <TableHead className="font-poppins text-slate-700">Sample No</TableHead>
                      <TableHead className="font-poppins text-slate-700">Product Name</TableHead>
                      <TableHead className="font-poppins text-slate-700">Client</TableHead>
                      <TableHead className="font-poppins text-slate-700">Status</TableHead>
                      <TableHead className="font-poppins text-slate-700">Iterations</TableHead>
                      <TableHead className="font-poppins text-slate-700">Created</TableHead>
                      <TableHead className="font-poppins text-slate-700">Sync</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.slice(0, 50).map((product) => (
                      <TableRow key={product.id} className="hover:bg-vcc-pink-50/30">
                        <TableCell className="font-poppins font-medium">{product.sampleNo || "-"}</TableCell>
                        <TableCell className="font-poppins">{product.productName || "-"}</TableCell>
                        <TableCell className="font-poppins text-slate-600">{product.clientName || "-"}</TableCell>
                        <TableCell>
                          <Badge className={`${statusColors[product.testStatus] || "bg-slate-100 text-slate-700"} font-poppins`}>
                            {product.testStatus || "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-poppins">{product.iterations?.length || 0}</TableCell>
                        <TableCell className="font-poppins text-slate-600">
                          {product.createdAt ? format(new Date(product.createdAt), "dd-MMM-yyyy") : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`font-poppins ${
                            product.syncStatus === "Synced" ? "border-emerald-500 text-emerald-600" :
                            product.syncStatus === "Failed" ? "border-red-500 text-red-600" :
                            "border-slate-300 text-slate-500"
                          }`}>
                            {product.syncStatus || "Pending"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredProducts.length > 50 && (
                  <div className="text-center py-4 text-slate-500 font-poppins text-sm">
                    Showing 50 of {filteredProducts.length} records. Export to view all.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
